import { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";

import { faColumns3, faPlus, faRadar, faSliders } from "@fortawesome/pro-regular-svg-icons";

import { Table, TableColumn } from "@/components/Table/Table";
import { TItem } from "@/components/SortableList/SortableList";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components";
import { useAddToListConfirm } from "@/components/AddToListConfirm/useAddToListConfirm";
import { InputField } from "@/components/InputField/InputField";
import { Radio } from "@/components/Radio/Radio";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Order, SortParams } from "@/types/types";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { useSnackBar } from "@/utils/useSnackbar";
import { useSearch } from "@/utils";
import { useAppSelector } from "@/redux/hooks";
import { ListSourceType } from "@/enum/list.enum";

import {
	useGetCompetitorsQuery,
	useCompetitorAddToListMutation,
} from "../../../redux/organization/organization.api";
import { competitorSelector } from "../../../redux/organization/organization.slice";
import { ContactsCompetitorFilters } from "../../Contacts/constants/contacts-filter";
import { useContactsUrlState } from "../../Contacts/hooks/useContactsUrlState";
import { useOrganizationSelection } from "../../Contacts/hooks/useOrganizationSelection";
import { useOrganizationTrackerAssignment } from "../../Contacts/hooks/useOrganizationTrackerAssignment";
import { CompetitorsTableColumns } from "./TableColumnConfigs/CompetitorsTableColumns";
import { AddToTrackerModal } from "../../Contacts/components/AddToTrackerModal";
import { SelectScopeModal } from "../../Contacts/components/SelectScopeModal";
import { AddCompetitorsModal } from "./AddCompetitorsModal";
import { UnflagCompetitorModal } from "./UnflagCompetitorModal";
import { useCompetitorUnflag } from "../hooks/useCompetitorUnflag";
import { useCompetitorsExport } from "../hooks/useCompetitorsExport";
import { ContactsColumnModal } from "../../Contacts/components/ContactsColumnModal";
import { ContactsFilterModal } from "../../Contacts/components/ContactsFilterModal";
import { buildOrganizationQueryParams } from "../../Contacts/utils/buildOrganizationQueryParams";
import { keepKnownFilters } from "../../Contacts/utils/keepKnownFilters";

export const CompetitorsTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`contacts.${key}`), [t]);

	const {
		page,
		setPage,
		limit,
		setLimit,
		sortingInbox,
		setSortingInbox,
		filter,
		setFilter,
		applyFilters,
		searchValue,
		setSearchValue,
		query,
	} = useContactsUrlState({
		prefix: "competitors",
		// Newest detections first: the dashboard hint sends the user here to review
		// what was flagged since their last visit.
		defaultSortBy: "score_company_category_result.createdAt",
		defaultSortOrder: Order.DESC,
		withSearch: true,
	}) as ReturnType<typeof useContactsUrlState> & {
		searchValue: string;
		setSearchValue: (v: string) => void;
		query: string;
		isTyping: boolean;
	};

	// A URL can carry a filter the competitors catalog no longer offers (e.g. a saved
	// `prospect.name`, which the organization query never joins); prune it once and hand the same
	// object to the query, the select-all payload and the modal.
	const competitorsFilter = useMemo(
		() => keepKnownFilters(filter, ContactsCompetitorFilters),
		[filter]
	);

	const [showEdit, setShowEdit] = useState<{ id: number; name: string } | undefined>(undefined);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		// v4: PREC-3781 reworked the columns (scoring badge leftmost, reason column removed —
		// its content lives on the DQ hover); older keys hold auto-persisted copies of prior orders.
		"contacts-columns-competitors-v4",
		{
			defaultValue: [],
		}
	);

	const { renderSnackbar, handleCopyClick } = useCopyToClipboard();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { competitors: data, competitorsMeta: meta } = useAppSelector(competitorSelector);

	const {
		selectedItems,
		selectAll,
		showAll,
		setShowAll,
		prepSelectType,
		setPrepSelectType,
		hasSelection,
		selectedCount,
		getSelectionPayload,
		toggleSelectAll,
		handleSetSelectType,
		handleSelectItem,
		clearSelection,
	} = useOrganizationSelection({
		data,
		itemCount: meta?.itemCount,
		filter: competitorsFilter,
		query,
	});

	const unflag = useCompetitorUnflag({
		clearSelection,
		getSelectionPayload,
		openSnackbar: handleOpenSnackbar,
	});

	// `refetchOnMountOrArgChange` is required, not a nicety: leaving the tab keeps the cache entry
	// warm for 60s and a cache hit dispatches no `fulfilled`, so the mirrored slice would keep
	// rendering the first visit's (possibly empty) result until a full page reload.
	const { isFetching } = useGetCompetitorsQuery(
		buildOrganizationQueryParams({ page, limit, sortingInbox, query, filter: competitorsFilter }),
		{ refetchOnMountOrArgChange: true }
	);

	const [addToList, { isLoading: isAddToListLoading }] = useCompetitorAddToListMutation();

	const handleExportError = useCallback(
		() => handleOpenSnackbar(t("basics.error")),
		[handleOpenSnackbar, t]
	);
	const { handleExport, isExportLoading } = useCompetitorsExport({
		getSelectionPayload,
		onError: handleExportError,
	});

	const tracker = useOrganizationTrackerAssignment({
		getSelectionPayload,
		openSnackbar: handleOpenSnackbar,
	});

	// ── Add to list ──────────────────────────────────────────────────────
	const addToListConfirm = useAddToListConfirm({
		selection: { count: selectedCount, source: "companies" },
		send: (target) => addToList({ ...getSelectionPayload(), ...target }).unwrap(),
		showMessage: handleOpenSnackbar,
	});

	// ── Table helpers ────────────────────────────────────────────────────
	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
	};

	const handleSorting = (sortParams: SortParams) => {
		setSortingInbox(sortParams);
	};

	const columns = useMemo(() => {
		let cols = CompetitorsTableColumns({
			handleCopyClick,
			ts,
			sortingInbox,
			setShowEdit,
			showEdit,
			setShowAll,
			toggleSelectAll,
			selectAll,
			selectedItems,
			handleSelectItem,
			onAddToTracker: tracker.open,
			onUnflagCompetitor: unflag.askForRow,
		});

		if (tableConfig?.length) {
			for (const c of cols) {
				const configIndex = tableConfig.findIndex((tc) => tc?.uid === c.uid);
				const currentIndex = cols.findIndex((tc) => tc?.uid === c.uid);

				if (configIndex > -1 && currentIndex > -1 && configIndex !== currentIndex) {
					const newConfigColumn = cols[currentIndex];

					cols.splice(currentIndex, 1);
					cols = [...cols.slice(0, configIndex), newConfigColumn, ...cols.slice(configIndex)];
				}
			}

			cols = cols.map((c) => {
				const config = tableConfig.find((tc) => tc?.uid === c?.uid);

				if (config) {
					c.show = config.show;
					c.locked = config.locked;
				}

				return c;
			});
		}

		return cols;
	}, [
		handleCopyClick,
		ts,
		sortingInbox,
		showEdit,
		tableConfig,
		selectAll,
		selectedItems,
		toggleSelectAll,
		handleSelectItem,
		unflag.askForRow,
	]);

	const resetTableConfig = (force = false) => {
		const sourceCols = force
			? CompetitorsTableColumns({
					handleCopyClick,
					ts,
					sortingInbox,
					setShowEdit,
					showEdit,
					setShowAll,
					toggleSelectAll,
					selectAll,
					selectedItems,
					handleSelectItem,
					onAddToTracker: tracker.open,
					onUnflagCompetitor: unflag.askForRow,
				})
			: columns;

		const newConfig: TItem[] = sourceCols.map((c: TableColumn, index: number) => ({
			uid: c.uid ?? "",
			id: index + 1,
			show: typeof c.show !== "undefined" ? c.show : true,
			content: c.title,
			locked: !!c.locked || false,
			allowExclude: typeof c.allowExclude !== "undefined" ? c.allowExclude : true,
			custom: false,
			moveable: c.moveable,
			hideable: c.hideable,
		}));

		if (tableConfig.length === 0 || force) {
			setTableConfig(newConfig);
		}
	};

	useMemo(() => {
		if (columns.length > 0 && tableConfig.length === 0) {
			resetTableConfig();
		}
	}, [columns]);

	return (
		<div className="w-full">
			<p className="px-2 py-2 text-xs text-text-meta bg-white border-b border-border-light">
				{ts("competitors.explainer")}
			</p>

			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						name="search"
						placeholder={ts("searchPlaceholder")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				<div className="ml-auto flex flex-row items-center gap-2">
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faColumns3} />}
							size={ButtonSize.S}
							title={ts("selectColumns")}
							onClick={() => setShowSelectColumns(true)}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faSliders} />}
							size={ButtonSize.S}
							title={`${ts("filter")}${competitorsFilter && Object.keys(competitorsFilter).length > 0 ? ` (${Object.keys(competitorsFilter).length})` : ""}`}
							onClick={() => setShowFilterModal(true)}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faPlus} />}
							size={ButtonSize.S}
							title={ts("competitors.addAction")}
							onClick={() => setShowAddModal(true)}
						/>
					</div>
				</div>
			</div>

			<Table
				addToAnotherList={
					hasSelection
						? {
								isLoading: isAddToListLoading,
								onCreateNew: addToListConfirm.onCreateNew,
								onSelectList: addToListConfirm.onSelectList,
							}
						: undefined
				}
				addToProfileTracker={
					hasSelection
						? {
								addToProfileTracker: () => tracker.open(),
								addToProfileTrackerIsLoading: tracker.isSaving,
							}
						: undefined
				}
				clearSelection={clearSelection}
				columns={columns}
				unflagCompetitor={
					hasSelection
						? {
								label: ts("competitors.unflagAction"),
								unflagCompetitor: () => unflag.askForSelection(selectedCount),
								unflagCompetitorIsLoading: unflag.isUnflagging,
							}
						: undefined
				}
				data={data || []}
				exportIsLoading={isExportLoading}
				filter={competitorsFilter}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleExport={hasSelection ? handleExport : undefined}
				handleFetchPage={setPage}
				handleSorting={handleSorting}
				isLoading={isFetching}
				limit={limit}
				overallCount={meta?.itemCount}
				page={page}
				selectedCount={selectedCount}
				showFooterFunctions={hasSelection}
				sticky
				tableConfig={tableConfig}
				totalPages={meta?.pageCount}
			/>

			{showSelectColumns && (
				<ContactsColumnModal
					handleResetToDefault={() => {
						setTableConfig([]);
						setTimeout(() => resetTableConfig(true), 100);
					}}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={tableConfig}
				/>
			)}

			{showFilterModal && (
				<ContactsFilterModal
					applyFilters={applyFilters}
					filter={competitorsFilter}
					filterDataType={ContactsCompetitorFilters}
					setFilter={setFilter}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
				/>
			)}

			{showAll && (
				<SelectScopeModal
					itemCount={meta?.itemCount}
					limit={limit}
					prepSelectType={prepSelectType}
					setPrepSelectType={setPrepSelectType}
					onClose={() => setShowAll(false)}
					onConfirm={() => handleSetSelectType(prepSelectType)}
				/>
			)}

			{addToListConfirm.modal}

			{tracker.isOpen && (
				<AddToTrackerModal
					isFetchingTrackerLists={tracker.isFetchingLists}
					isLoadingTrackerLists={tracker.isLoadingLists}
					isSaving={tracker.isSaving}
					selectedTracker={tracker.selectedTracker}
					trackerLists={tracker.lists}
					onClose={() => {
						tracker.close();
					}}
					onConfirm={tracker.confirm}
					onSearch={tracker.setSearchValue}
					onSelect={tracker.setSelectedTracker}
				/>
			)}

			{showAddModal && <AddCompetitorsModal isOpen={showAddModal} setIsOpen={setShowAddModal} />}

			{unflag.pending && (
				<UnflagCompetitorModal
					companyName={unflag.pending.companyName}
					count={unflag.pending.count}
					isLoading={unflag.isUnflagging}
					onClose={unflag.cancel}
					onConfirm={unflag.confirm}
				/>
			)}

			{renderSnackbar()}
			{renderMessageSnackbar({ onClick: () => unflag.undo(unflag.undoableIds) })}
		</div>
	);
};
